import React, { useState, useContext, useEffect, useRef } from "react";
import askNiki from "../../assets/askNiki.svg";
import audio from "../../assets/audio.svg";
import { Link } from "react-router-dom";
import { LuRefreshCcw } from "react-icons/lu";
import { updateKnowledge, getSharepointSyncStatus } from "../../utils/services/api";
import toast from "react-hot-toast";
import { AppContext } from "../../utils/Context/AppContext";

const SYNC_POLL_INTERVAL_MS = 15000;

const Main = () => {
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState("");
  const { setDisclaimerModal } = useContext(AppContext);
  const pollTimerRef = useRef(null);

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const pollUntilDone = () => {
    stopPolling();
    pollTimerRef.current = setInterval(async () => {
      try {
        const { status, current_step } = await getSharepointSyncStatus();
        if (current_step) {
          setCurrentStep(current_step);
        }
        if (status !== "running") {
          stopPolling();
          setLoading(false);
          setCurrentStep("");
          toast.success("Knowledge base update completed");
        }
      } catch (error) {
        // A transient poll failure shouldn't leave the button stuck
        // disabled forever — just try again on the next tick.
      }
    }, SYNC_POLL_INTERVAL_MS);
  };

  // On mount, pick up an already-in-progress sync (e.g. someone else
  // triggered it, or this page was reloaded mid-sync) so the button stays
  // correctly disabled instead of looking clickable while work is ongoing.
  useEffect(() => {
    (async () => {
      try {
        const { status, current_step } = await getSharepointSyncStatus();
        if (status === "running") {
          setLoading(true);
          setCurrentStep(current_step || "Update in progress...");
          pollUntilDone();
        }
      } catch (error) {
        // No status available (e.g. older backend) — leave the button
        // enabled, same as before this feature existed.
      }
    })();
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The backend kicks the sync off in the background and responds
  // immediately — a synchronous multi-minute sync here used to hit nginx's
  // gateway timeout (504) once there were enough files needing processing.
  // The button stays disabled for the whole run (not just the initial
  // request) by polling /sharepoint_sync_status until it flips back to idle.
  const handleUpdateKnowledgebase = async () => {
    let toastId;
    try {
      setLoading(true);
      setCurrentStep("Starting...");
      toastId = toast.loading("Starting...");
      const response = await updateKnowledge();
      toast.dismiss(toastId);
      if (response?.data?.already_running) {
        toast("A knowledge base update is already running — please wait for it to finish.");
      } else {
        toast.success("Knowledge base update started — this runs in the background and can take several minutes");
      }
      pollUntilDone();
    } catch (error) {
      toast.dismiss(toastId);
      toast.error(`Error: ${error}`);
      setLoading(false);
      setCurrentStep("");
    }
  };

  return (
    <div className="relative flex flex-col items-center lg:justify-center justify-start gap-6 h-full w-full bg-slate-100 px-4 py-6 overflow-auto">
      {/* Update Button - Absolute on large screens, static on small */}
      <div className="w-full flex flex-col items-end gap-1 lg:absolute lg:top-6 lg:right-6 z-10">
        <button
          onClick={handleUpdateKnowledgebase}
          disabled={loading}
          className="text-[#00AEEF] text-[16px] flex items-center gap-2 font-semibold px-3 py-2 hover:bg-[#00AEEF] hover:text-white bg-white rounded-[4px] border border-[#00AEEF] transition-colors duration-300 ease-in-out disabled:opacity-50"
        >
          <LuRefreshCcw className={loading ? "animate-spin" : ""} />
          Update Knowledge base
        </button>
        {loading && (
          <span className="text-xs text-[#3A3A3A] bg-white px-2 py-1 rounded-[4px] shadow-sm">
            {currentStep || "Working on it..."}
          </span>
        )}
      </div>

      {/* Card Row */}
      <div className="flex flex-col lg:flex-row justify-center items-stretch gap-6 w-full">
        {/* Ask Niki Card */}
        <Link
          to="/chat?new_conversation"
          onClick={() => setDisclaimerModal(true)}
          className="w-full lg:w-1/2 flex"
        >
          <div className="flex flex-col p-4 md:p-6 bg-white rounded-lg cursor-pointer shadow-md w-full h-full">
            <img src={askNiki} alt="ask niki logo" className="w-full h-auto" />
            <span className="font-bold text-[#3A3A3A] text-lg md:text-xl mt-4">
              Ask Niki!
            </span>
            <span className="text-xs md:text-sm mt-2 whitespace-pre-line text-[#5e5e5e] text-justify min-h-[56px]">
              Our Intelligent AI Companion, Revolutionizing Conversations and
              Streamlining Solutions for a Smarter, Effortless Experience.
            </span>
          </div>
        </Link>

        {/* Audio Summarizer Card */}
        <Link to="/upload-audio" className="w-full lg:w-1/2 flex">
          <div className="flex flex-col p-4 md:p-6 bg-white rounded-lg cursor-pointer shadow-md w-full h-full">
            <img
              src={audio}
              alt="audio summarizer logo"
              className="w-full h-auto"
            />
            <span className="font-bold text-[#3A3A3A] text-lg md:text-xl mt-4">
              Audio Summarizer
            </span>
            <span className="text-xs md:text-sm mt-2 whitespace-pre-line text-[#5e5e5e] text-justify">
              Effortlessly Analyzing Hours of Audio to Deliver Precise, Concise
              Summaries and Actionable Insights in Just Seconds, Saving Time and
              Enhancing Productivity.
            </span>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default Main;
